import {useEffect, useMemo, useState} from "react";

import {debounce} from "lodash";

import {Material_File_Type, useMaterialFilesQuery} from "@generated/graphql";
import {buildFileTree} from "../helpers";
import type {MaterialFileItem} from "../types";

const compareMaterialFilesByOrder = (firstFile: MaterialFileItem, secondFile: MaterialFileItem) => {
  const firstOrder = Number(firstFile.order);
  const secondOrder = Number(secondFile.order);

  if (Number.isFinite(firstOrder) && Number.isFinite(secondOrder)) {
    return firstOrder - secondOrder;
  }

  return firstFile.order.localeCompare(secondFile.order, undefined, {numeric: true});
};

const useMaterialFirstViewFiles = (materialId: string) => {
  const [searchInput, setSearchInput] = useState<string>("");
  const [materialFilesSearch, setMaterialFilesSearch] = useState<string>("");
  const searchInputValue = searchInput.trim();
  const isSearchActive = materialFilesSearch.length > 0;
  const isSearchPending = searchInputValue !== materialFilesSearch;

  const materialFilesFilter = useMemo(
    () => materialFilesSearch ? {search: materialFilesSearch} : undefined,
    [materialFilesSearch],
  );
  const {data, loading} = useMaterialFilesQuery({
    variables: {
      materialId,
      filter: materialFilesFilter,
    },
  });

  const materialFiles = useMemo(
    () => [...(data?.materialFiles ?? [])].sort(compareMaterialFilesByOrder),
    [data?.materialFiles],
  );
  const recommendationFiles = useMemo(
    () => materialFiles.filter((file) => file.fileType === Material_File_Type.Recommendation),
    [materialFiles],
  );
  const otherFileTree = useMemo(
    () => buildFileTree(materialFiles.filter((file) => file.fileType !== Material_File_Type.Recommendation)),
    [materialFiles],
  );
  const updateMaterialFilesSearch = useMemo(
    () => debounce((value: string) => setMaterialFilesSearch(value.trim()), 300),
    [],
  );

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
    updateMaterialFilesSearch(value);
  }

  useEffect(() => {
    return () => {
      updateMaterialFilesSearch.cancel();
    };
  }, [updateMaterialFilesSearch]);

  return {
    searchInput,
    handleSearchInputChange,
    materialFiles,
    recommendationFiles,
    otherFileTree,
    isSearchActive,
    loading: loading || isSearchPending,
  };
};

export default useMaterialFirstViewFiles;
